import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    container: {
        padding: 10,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 15,
        color: '#1e40af',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        paddingBottom: 5,
    },
    subsectionTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        marginTop: 15,
        marginBottom: 10,
        color: '#374151',
        backgroundColor: '#f3f4f6',
        padding: 8,
    },
    table: {
        display: 'flex',
        width: '100%',
        marginBottom: 15,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
        paddingVertical: 8,
    },
    tableHeader: {
        backgroundColor: '#f9fafb',
        fontWeight: 'bold',
    },
    labelCell: {
        width: '40%',
        paddingRight: 10,
        fontSize: 11,
        fontWeight: 'bold',
        color: '#374151',
    },
    valueCell: {
        width: '60%',
        fontSize: 11,
        color: '#111827',
    },
    required: {
        color: '#dc2626',
    },
    kValueSection: {
        marginTop: 20,
        padding: 10,
        backgroundColor: '#f0f9ff',
        borderRadius: 5,
        borderWidth: 1,
        borderColor: '#0ea5e9',
    },
    kValueText: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#0c4a6e',
    },
});

interface PackageStagePDFProps {
    data: {
        has_tertiary_package: string;
        secondary_package_type: string;
        secondary_topload_filled?: string;
        secondary_topload_filled_unit?: string;
        secondary_weight: string;
        secondary_weight_unit: string;
        secondary_length: string;
        secondary_length_unit: string;
        secondary_width: string;
        secondary_width_unit: string;
        secondary_height: string;
        secondary_height_unit: string;
        tertiary_material?: string;
        tertiary_bct?: string;
        tertiary_bct_unit?: string;
        tertiary_length?: string;
        tertiary_length_unit?: string;
        tertiary_width?: string;
        tertiary_width_unit?: string;
        tertiary_height?: string;
        tertiary_height_unit?: string;
        tertiary_weight?: string;
        tertiary_weight_unit?: string;
        k_value: string;
    };
}

export const PackageStagePDF: React.FC<PackageStagePDFProps> = ({ data }) => {
    const formatValue = (value: string | undefined, unit?: string) => {
        if (!value) return 'N/A';
        return unit ? `${value} ${unit}` : value;
    };

    const hasTertiaryPackage = data.has_tertiary_package === 'yes';

    return (
        <View style={styles.container}>
            <Text style={styles.sectionTitle}>Package Details</Text>

            {/* Secondary Package Details */}
            <Text style={styles.subsectionTitle}>Secondary Package Details</Text>
            <View style={styles.table}>
                {/* <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={styles.labelCell}>Field</Text>
                    <Text style={styles.valueCell}>Value</Text>
                </View> */}

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Secondary Package Type <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{data.secondary_package_type || 'N/A'}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Secondary Topload Filled</Text>
                    <Text style={styles.valueCell}>{formatValue(data.secondary_topload_filled, data.secondary_topload_filled_unit)}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Secondary Weight <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{formatValue(data.secondary_weight, data.secondary_weight_unit)}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Secondary Length <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{formatValue(data.secondary_length, data.secondary_length_unit)}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Secondary Width <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{formatValue(data.secondary_width, data.secondary_width_unit)}</Text>
                </View>

                <View style={styles.tableRow}>
                    <Text style={styles.labelCell}>Secondary Height <Text style={styles.required}>*</Text></Text>
                    <Text style={styles.valueCell}>{formatValue(data.secondary_height, data.secondary_height_unit)}</Text>
                </View>
            </View>

            {/* Tertiary Package Details - Only show if has tertiary package */}
            {hasTertiaryPackage && (
                <>
                    <Text style={styles.subsectionTitle}>Tertiary Package Details</Text>
                    <View style={styles.table}>
                        {/* <View style={[styles.tableRow, styles.tableHeader]}>
                            <Text style={styles.labelCell}>Field</Text>
                            <Text style={styles.valueCell}>Value</Text>
                        </View> */}

                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Tertiary Material <Text style={styles.required}>*</Text></Text>
                            <Text style={styles.valueCell}>{data.tertiary_material || 'N/A'}</Text>
                        </View>

                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Tertiary BCT</Text>
                            <Text style={styles.valueCell}>{formatValue(data.tertiary_bct, data.tertiary_bct_unit)}</Text>
                        </View>

                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Tertiary Length <Text style={styles.required}>*</Text></Text>
                            <Text style={styles.valueCell}>{formatValue(data.tertiary_length, data.tertiary_length_unit)}</Text>
                        </View>

                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Tertiary Width <Text style={styles.required}>*</Text></Text>
                            <Text style={styles.valueCell}>{formatValue(data.tertiary_width, data.tertiary_width_unit)}</Text>
                        </View>

                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Tertiary Height <Text style={styles.required}>*</Text></Text>
                            <Text style={styles.valueCell}>{formatValue(data.tertiary_height, data.tertiary_height_unit)}</Text>
                        </View>

                        <View style={styles.tableRow}>
                            <Text style={styles.labelCell}>Tertiary Weight <Text style={styles.required}>*</Text></Text>
                            <Text style={styles.valueCell}>{formatValue(data.tertiary_weight, data.tertiary_weight_unit)}</Text>
                        </View>
                    </View>
                </>
            )}

            {/* K-Value Section */}
            {/* <View style={styles.kValueSection}>
                <Text style={styles.kValueText}>
                    Required Nearest Neighbours (K-Value): {data.k_value || 'N/A'}
                </Text>
            </View> */}
        </View>
    );
}; 